<template>
  <div class="cv-root" :class="{ 'cv-dark': isDark, 'cv-preview': isPreview }">
    <div v-if="!isPreview" class="cv-toolbar no-print">
      <button
        v-if="backTarget"
        type="button"
        class="cv-back"
        @click="returnToOrigin"
      >
        <span class="cv-back-arrow" aria-hidden="true">←</span>
        <span>Back to {{ backLabel }}</span>
      </button>
      <div class="cv-toolbar-actions">
        <button
          type="button"
          class="cv-theme"
          :title="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
          :aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
          @click="toggleTheme"
        >
          <i :class="isDark ? 'fa fa-sun' : 'fa fa-moon'" aria-hidden="true"></i>
        </button>
        <button type="button" class="cv-print" @click="printPage">Print / Save PDF</button>
      </div>
    </div>

    <article class="cv-sheet">
      <!-- Masthead -->
      <header class="cv-head">
        <h1 class="cv-name">Henry Han-yu Wang</h1>
        <p class="cv-tagline">Applied AI undergraduate, <span class="nowrap">University of Hong Kong</span></p>
        <p class="cv-contact">
          <a href="mailto:whenry6688@gmail.com">whenry6688@gmail.com</a>
          <span class="dot">&middot;</span>
          <a href="https://www.henryw.me/" target="_blank" rel="noopener">henryw.me</a>
          <span class="dot">&middot;</span>
          <a href="https://www.linkedin.com/in/henrywhy/" target="_blank" rel="noopener">LinkedIn</a>
          <span class="dot">&middot;</span>
          <a href="https://github.com/henryhyw/" target="_blank" rel="noopener">GitHub</a>
        </p>
      </header>

      <!-- Profile -->
      <section id="publications-preprints" class="cv-section">
        <h2 class="cv-label">Profile</h2>
        <div class="cv-body">
          <p>
            Fourth-year Applied Artificial Intelligence student at the University of Hong Kong. I&rsquo;m interested in how
            people form and use knowledge, how representations and internal processes support behaviour in AI systems, and how
            learning and thinking change when people work with AI.
          </p>
          <p>
            At HKU’s Speech, Language and Reading Lab, I build computational models of how
            people form representations through learning and use them to transfer knowledge
            across related tasks. I am also a Cloud, Data &amp; AI consulting intern at PwC,
            where I work on solution architecture and development of AI and data systems for
            financial-services and public-sector clients.
          </p>
        </div>
      </section>

      <!-- Education -->
      <section class="cv-section">
        <h2 class="cv-label">Education</h2>
        <div class="cv-body">
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">University of Hong Kong</p>
              <span class="entry-meta">Sep 2022 &ndash; Present</span>
            </div>
            <p class="entry-sub">BASc in Applied Artificial Intelligence</p>
            <p class="entry-note">
              Coursework across machine learning, deep learning, data science, algorithms, and the mathematics behind them.
            </p>
            <p class="entry-note">
              Dean&rsquo;s Honours List, Student Peer Advisor, and Staff&ndash;Student Consultative Committee representative.
            </p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">University of Melbourne</p>
              <span class="entry-meta">Feb 2025 &ndash; Jun 2025</span>
            </div>
            <p class="entry-sub">Semester exchange, HKU Worldwide Student Exchange</p>
            <p class="entry-note">Optimisation, Machine Learning, Principles of Finance.</p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">Shanghai High School International Division</p>
              <span class="entry-meta">Sep 2017 &ndash; Jun 2022</span>
            </div>
            <p class="entry-sub">International Baccalaureate Diploma</p>
            <p class="entry-note">Computer science, mathematics, economics, physics. Led the Coding Club and co-founded the SHSID Musix Board.</p>
          </div>
        </div>
      </section>

      <!-- Experience -->
      <section class="cv-section">
        <h2 class="cv-label">Experience</h2>
        <div class="cv-body">
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">Cloud, Data &amp; AI Consulting Intern</p>
              <span class="entry-meta">Jul 2026 &ndash; Dec 2026</span>
            </div>
            <p class="entry-sub">PwC, Hong Kong</p>
            <p class="entry-note">
              Designed an AI-assisted knowledge management architecture that identifies gaps,
              acquires missing material and keeps technical guidance up to date. Built a
              structure-preserving retrieval-augmented generation (RAG) ingestion pipeline for
              complex documents and a pipeline using a large language model (LLM) to recover
              legacy extract, transform and load (ETL) logic for data migration.
            </p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">Student Research Assistant</p>
              <span class="entry-meta">May 2024 &ndash; Present</span>
            </div>
            <p class="entry-sub">Speech, Language &amp; Reading Lab, HKU</p>
            <p class="entry-note">
              Built computational models of human learning and cognition, using behavioural data to study how learned representations support generalisation and transfer.
            </p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">Digital Transformation Intern</p>
              <span class="entry-meta">Jul 2024 &ndash; Dec 2024</span>
            </div>
            <p class="entry-sub">HSBC, Hong Kong</p>
            <p class="entry-note">
              Consolidated multi-market product and distribution information, then built an Excel/VBA configuration engine that
              mapped insurance product specifications through business scenarios and decision tables to UI-component requirements.
            </p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title">Web Developer, Digital Content Support</p>
              <span class="entry-meta">Jun 2023 &ndash; Dec 2023</span>
            </div>
            <p class="entry-sub">Kiwiview International Limited, New Zealand</p>
            <p class="entry-note">Designed, developed, deployed, and maintained the company website, and produced branding and content for travel programmes.</p>
          </div>
        </div>
      </section>

      <!-- Publications -->
      <section class="cv-section">
        <h2 class="cv-label">Publications &amp; Preprints</h2>
        <div class="cv-body">
          <p class="pub-group" hidden>Human-AI interaction and collaboration</p>
          <ul class="pub-list">
            <li>
              <a class="pub-title" href="https://arxiv.org/abs/2609.09332" target="_blank" rel="noopener">Early Epistemic Settlement in AI-Assisted Writing</a>
              <span class="pub-authors"><span class="me">H. Wang</span></span>
              <span class="pub-venue">Under review · arXiv:2609.09332</span>
              <div class="pub-tip no-print" v-html="pubDesc.fittedAssistance"></div>
            </li>
          </ul>
          <p class="pub-group" hidden>Human learning and computational cognition</p>
          <ul class="pub-list" hidden>
            <li class="publication-hidden">
              <span class="pub-title">Functional Encoding and Representational Binding in Componential Transfer</span>
              <span class="pub-authors">S. M. K. Lee, <span class="me">H. Wang</span></span>
              <span class="pub-venue">Under review</span>
              <div class="pub-tip no-print" v-html="pubDesc.encoding"></div>
            </li>
            <li class="publication-hidden">
              <span class="pub-title">When Does a Smooth Learning Curve Mean Smooth Learning? Representational Format, Identifiability, and the Bounded Averaging Artifact</span>
              <span class="pub-authors"><span class="me">H. Wang</span></span>
              <span class="pub-venue">Under review</span>
              <div class="pub-tip no-print" v-html="pubDesc.smoothLearningCurve"></div>
            </li>
          </ul>
          <p class="pub-group" hidden>Language models, behavior, and interpretability</p>
          <ul class="pub-list">
            <li class="publication-hidden" hidden>
              <a class="pub-title" href="https://arxiv.org/abs/2606.07555" target="_blank" rel="noopener">Persistent Priors, Preserved Targets: A Stroop-Style Paradigm for Lexical Override</a>
              <span class="pub-authors"><span class="me">H. Wang</span></span>
              <span class="pub-venue">Under review; arXiv:2606.07555</span>
              <div class="pub-tip no-print" v-html="pubDesc.priorsPersist"></div>
            </li>
            <li>
              <a class="pub-title" href="https://arxiv.org/abs/2407.12883" target="_blank" rel="noopener">BRIGHT: A Realistic and Challenging Benchmark for Reasoning-Intensive Retrieval</a>
              <span class="pub-authors">H. Su*, H. Yen*, M. Xia*, W. Shi, N. Muennighoff, <span class="me">H. Wang</span>, H. Liu, Q. Shi, Z. S. Siegel, M. Tang, R. Sun, J. Yoon, S. O. Arik, D. Chen, T. Yu</span>
              <span class="pub-venue">ICLR 2025 Spotlight; arXiv:2407.12883</span>
              <div class="pub-tip no-print" v-html="pubDesc.bright"></div>
            </li>
          </ul>
        </div>
      </section>

      <!-- Projects -->
      <section id="projects" class="cv-section">
        <h2 class="cv-label">Projects</h2>
        <div class="cv-body">
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title"><a href="https://www.henryw.me/#/aipersonalarchive" target="_blank" rel="noopener">AI × Self</a></p>
              <span class="entry-meta">Jun 2026 &ndash; Present</span>
            </div>
            <p class="entry-sub">An AI-driven personal archive</p>
            <p class="entry-note">
              Built a personal archive combining hybrid search, retrieval-augmented answers and AI-composed Views.
              Each View connects my work, ideas and experiences around a visitor&rsquo;s question and becomes an
              illustrated article and an interactive 3D exhibition.
            </p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title"><a href="https://cathayflex.github.io/" target="_blank" rel="noopener">Cathay Flex</a></p>
              <span class="entry-meta">Oct 2026 &ndash; Present</span>
            </div>
            <p class="entry-sub">Cathay Pacific Hackathon</p>
            <p class="entry-note">
              Developed an airline-managed marketplace where travellers earn credits for flexibility and use them across
              services and journeys. AI interprets passenger preferences, constrained optimisation finds compatible
              arrangements, and a transaction and settlement system manages agreements
              through a conditional order book.
            </p>
          </div>
          <div class="entry">
            <div class="entry-head">
              <p class="entry-title"><a href="https://slidepoise.github.io/" target="_blank" rel="noopener">SlidePoise</a></p>
              <span class="entry-meta">Aug 2026 &ndash; Sep 2026</span>
            </div>
            <p class="entry-sub">Open-source agentic slide generation</p>
            <p class="entry-note">
              Built an open-source framework for generating editable PowerPoint presentations with an AI agent.
              Image generation explores slide designs, then semantic interpretation and OpenCV measurement guide
              their reconstruction as native text, charts, tables and shapes.
            </p>
          </div>
        </div>
      </section>

      <footer class="cv-foot no-print">
        <span>Henry Han-yu Wang</span>
        <span>Last updated October 2026</span>
      </footer>
    </article>
  </div>
</template>

<script>
import { publicationDescriptions } from '../data/publications';
import { getSiteOrigin, markTabNavigation } from '../utils/askSession';
import { settleScrollToY } from '../utils/anchorLanding';

export default {
  name: 'ResumeTab',
  data() {
    return {
      pubDesc: publicationDescriptions,
      isDark: false,
      backTarget: '',
      backLabel: ''
    };
  },
  computed: {
    isPreview() {
      return this.$route.query.preview === '1';
    }
  },
  mounted() {
    const origin = getSiteOrigin();
    this.backTarget = origin && origin.route ? origin.route : '/home';
    this.backLabel = origin && origin.label ? origin.label : 'Home';
    this.syncThemeState();
    this._onThemeChange = () => this.syncThemeState();
    window.addEventListener('theme-change', this._onThemeChange);
    this._clampPubTip = (li, tip) => {
      requestAnimationFrame(() => {
        // The tip only becomes display:block when :hover lands; measure on the
        // frame after layout so the box has its real size.
        requestAnimationFrame(() => {
          const lr = li.getBoundingClientRect();
          const tipHeight = tip.getBoundingClientRect().height || 315;
          const vh = window.innerHeight;
          let top = 0;
          if (lr.top + tipHeight > vh - 8) {
            const overflow = lr.top + tipHeight - (vh - 8);
            const shift = Math.min(overflow, Math.max(0, tipHeight - 48));
            top = -shift;
          }
          tip.style.top = `${top}px`;
        });
      });
    };
    this._onPubEnter = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const li = target.closest('.pub-list li');
      if (!li) return;
      this._lastPubLi = li;
      const tip = li.querySelector('.pub-tip');
      if (!tip) return;
      this._clampPubTip(li, tip);
    };
    document.addEventListener('mouseenter', this._onPubEnter, true);
    this._onPubScroll = () => {
      if (!this._lastPubLi) return;
      const tip = this._lastPubLi.querySelector('.pub-tip');
      if (tip && getComputedStyle(tip).display !== 'none') {
        this._clampPubTip(this._lastPubLi, tip);
      }
    };
    window.addEventListener('scroll', this._onPubScroll, { passive: true });
  },
  unmounted() {
    window.removeEventListener('theme-change', this._onThemeChange);
    document.removeEventListener('mouseenter', this._onPubEnter, true);
    window.removeEventListener('scroll', this._onPubScroll);
    if (this._themeTransitionTimer) {
      clearTimeout(this._themeTransitionTimer);
      this._themeTransitionTimer = null;
    }
  },
  methods: {
    returnToOrigin() {
      const target = this.backTarget;
      if (!target) return;
      const scrollY = Number((getSiteOrigin() && getSiteOrigin().scrollY) || 0);
      markTabNavigation();
      const hasAnchor = target.includes('#');
      this.$router.push(target).catch(() => {});
      if (!hasAnchor) {
        settleScrollToY(scrollY);
      }
    },
    syncThemeState() {
      const saved = localStorage.getItem('theme');
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const shouldBeDark = document.body.classList.contains('dark-mode') || saved === 'dark' || (!saved && prefersDark);
      document.documentElement.classList.toggle('dark-mode', shouldBeDark);
      document.body.classList.toggle('dark-mode', shouldBeDark);
      this.isDark = shouldBeDark;
      this.updateGlobalThemeIcon();
    },
    applyTheme(theme, persist = true) {
      const dark = theme === 'dark';
      document.documentElement.classList.toggle('dark-mode', dark);
      document.body.classList.toggle('dark-mode', dark);
      this.isDark = dark;
      this.updateGlobalThemeIcon();
      if (persist) {
        localStorage.setItem('theme', theme);
      }
      window.dispatchEvent(new CustomEvent('theme-change', { detail: { theme } }));
    },
    updateGlobalThemeIcon() {
      const icon = document.querySelector('#toggle-theme i');
      if (!icon) return;
      icon.classList.toggle('fa-sun', this.isDark);
      icon.classList.toggle('fa-moon', !this.isDark);
    },
    toggleTheme() {
      this.beginThemeTransition();
      this.applyTheme(this.isDark ? 'light' : 'dark');
    },
    beginThemeTransition() {
      document.documentElement.classList.add('theme-transitioning');
      document.body.classList.add('theme-transitioning');
      clearTimeout(this._themeTransitionTimer);
      this._themeTransitionTimer = setTimeout(() => {
        document.documentElement.classList.remove('theme-transitioning');
        document.body.classList.remove('theme-transitioning');
        this._themeTransitionTimer = null;
      }, 280);
    },
    printPage() {
      window.print();
    }
  }
};
</script>

<style scoped>
/* ---- Page canvas ---- */
.cv-root {
  --ink: #141414;
  --muted: #6b6b6b;
  --faint: #9a9a9a;
  --rule: #e6e6e6;
  --bg: #ffffff;
  --note: #3c3c3c;
  --tip-bg: #ffffff;
  --tip-shadow: none;
  --button-hover-text: #ffffff;
  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  padding: 0 20px 48px;
}

.cv-preview {
  padding-top: 14px;
}

.cv-root.cv-dark,
:global(body.dark-mode .cv-root) {
  --ink: #cccccc;
  --muted: #9a9a9a;
  --faint: #666666;
  --rule: #2f2f2f;
  --bg: #1c1c1e;
  --note: #cfcfcf;
  --tip-bg: #2c2c2e;
  --tip-shadow: none;
  --button-hover-text: #000000;
}

/* Toolbar above the sheet */
.cv-toolbar {
  max-width: 740px;
  margin: 0 auto;
  padding: 22px 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cv-toolbar-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.cv-back {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.76rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  color: var(--muted);
  cursor: pointer;
  transition: color 140ms ease, transform 120ms ease;
}
.cv-back:hover { color: var(--ink); }
.cv-back:hover .cv-back-arrow,
.cv-back:focus-visible .cv-back-arrow { transform: translateX(-2px); }
.cv-back-arrow {
  font-size: 1rem;
  line-height: 1;
  transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.cv-back:hover { color: var(--ink); }
.cv-theme,
.cv-print {
  --cv-action-height: 2rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.74rem;
  line-height: 1;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink);
  background: transparent;
  border: 1px solid var(--ink);
  border-radius: 2px;
  height: var(--cv-action-height);
  padding: 7px 14px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.cv-theme {
  width: var(--cv-action-height);
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.cv-theme:hover,
.cv-print:hover {
  background: var(--ink);
  color: var(--button-hover-text);
}

/* The sheet */
.cv-sheet {
  max-width: 740px;
  margin: 0 auto;
  font-family: "PT Serif", Georgia, serif;
  font-size: 15px;
  line-height: 1.6;
}

/* Masthead */
.cv-head {
  text-align: center;
  padding-bottom: 26px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--rule);
}
.cv-name {
  font-family: "PT Serif", Georgia, serif;
  font-weight: 700;
  font-size: 2.5rem;
  line-height: 1.1;
  letter-spacing: 0.01em;
  color: var(--ink);
  margin: 0;
}
.cv-tagline {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.92rem;
  color: var(--muted);
  margin: 10px 0 14px;
}
.cv-tagline .nowrap { white-space: nowrap; }
.cv-contact {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  color: var(--muted);
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
}
.cv-contact a {
  color: var(--ink);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s ease;
}
.cv-contact a:hover { border-bottom-color: var(--ink); }
.cv-contact .dot { margin: 0 9px; color: var(--faint); }

/* Sections: label left, content right */
.cv-section {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 16px;
  padding: 26px 0;
  border-bottom: 1px solid var(--rule);
}
.cv-section:last-of-type { border-bottom: none; }
.cv-label {
  font-family: "Quattrocento Sans", sans-serif;
  font-weight: 400;
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--faint);
  margin: 4px 0 0;
}
.cv-body { margin: 0; }
.cv-body > p:first-child { margin-top: 0; }
.cv-body p { margin: 0; }

/* Timeline entries: title left, date right-aligned on the same row */
.entry { margin-bottom: 20px; }
.entry:last-child { margin-bottom: 0; }
.entry-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
}
.entry-title {
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 1px;
}
.entry-title a {
  color: inherit;
  text-decoration: none;
  text-underline-offset: 3px;
}
.entry-title a:hover,
.entry-title a:focus-visible {
  text-decoration: underline;
}
.entry-meta {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.76rem;
  letter-spacing: 0.02em;
  color: var(--faint);
  white-space: nowrap;
  flex-shrink: 0;
}
.incoming {
  font-family: "Quattrocento Sans", sans-serif;
  font-weight: 400;
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
  border: 1px solid var(--rule);
  border-radius: 2px;
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
}
.entry-sub {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.85rem;
  color: var(--muted);
  margin: 0 0 5px;
}
.entry-note {
  font-size: 0.92rem;
  color: var(--note);
  margin: 0;
}

/* Publications */
.cv-body .pub-group {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.64rem;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--faint);
  margin: 0 0 11px;
}
.pub-list + .pub-group {
  margin-top: 22px;
}
.pub-list ~ .pub-group[hidden] + .pub-list {
  margin-top: 18px;
}
.pub-list {
  list-style: none;
  margin: 0;
  padding-left: 0;
}
.pub-list li {
  position: relative;
  margin-bottom: 18px;
  font-size: 0.92rem;
  color: var(--note);
  line-height: 1.5;
}
.pub-list li.publication-hidden {
  display: none;
}
.pub-list li:last-child { margin-bottom: 0; }

/* Hover tooltip: the project's full description, shared with the Research page. */
.pub-tip {
  position: absolute;
  display: none;
  left: calc(100% + 10px);
  top: 0;
  z-index: 20;
  width: 320px;
  max-width: min(320px, calc(100vw - 40px));
  box-sizing: border-box;
  padding: 14px 16px;
  background: var(--tip-bg);
  border: 1px solid var(--rule);
  border-radius: 5px;
  box-shadow: var(--tip-shadow);
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.82rem;
  line-height: 1.55;
  color: var(--note);
  opacity: 0;
  transform: translateY(-4px);
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

/* The right-side tooltip only fits on wide viewports; hide it below that. */
@media (max-width: 1399px) {
  .pub-tip {
    display: none !important;
  }
}

.pub-tip::before {
  content: "";
  position: absolute;
  left: -5px;
  top: 18px;
  width: 9px;
  height: 9px;
  background: var(--tip-bg);
  border-left: 1px solid var(--rule);
  border-top: 1px solid var(--rule);
  transform: rotate(45deg);
}
.pub-list li:hover .pub-tip,
.pub-list li:focus-within .pub-tip {
  display: block;
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0.5s;
}
.pub-title {
  display: block;
  color: var(--ink);
  text-decoration: none;
}
.pub-title:hover,
.pub-title:focus {
  text-decoration: none;
}
.pub-authors {
  display: block;
  color: var(--note);
}
.pub-venue {
  display: block;
  font-style: italic;
  color: var(--muted);
}
.me { font-weight: 700; color: var(--ink); }

/* Bottom line */
.cv-foot {
  display: flex;
  justify-content: space-between;
  margin-top: 34px;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  color: var(--faint);
}

/* Responsive: stack the label column; date sits above the title */
@media (max-width: 620px) {
  .cv-name { font-size: 2rem; }
  .cv-section { grid-template-columns: 1fr; gap: 10px; }
  .cv-label { margin-bottom: 4px; }
  .entry-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
  }
  .entry-meta { order: -1; }
}

/* Print: pure paper, no toolbar */
@media print {
  .no-print { display: none !important; }
  :global(html.dark-mode),
  :global(html.dark-mode body),
  :global(html.dark-mode #app),
  :global(body.dark-mode),
  :global(body.dark-mode #app) {
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #141414 !important;
  }
  .cv-root,
  .cv-root.cv-dark,
  :global(body.dark-mode .cv-root) {
    --ink: #141414;
    --muted: #6b6b6b;
    --faint: #9a9a9a;
    --rule: #e6e6e6;
    --bg: #ffffff;
    --note: #3c3c3c;
    --tip-bg: #ffffff;
    --button-hover-text: #ffffff;
    padding: 0;
    background: #ffffff !important;
    color: #141414 !important;
  }
  .cv-root * {
    transition: none !important;
  }
  .cv-sheet { max-width: 100%; font-size: 11pt; }
  .cv-head,
  .cv-section {
    border-bottom: none;
  }
  .cv-section { break-inside: avoid; }
  .entry, .pub-list li { break-inside: avoid; }
  a { color: #000 !important; }
}
</style>

<style>
html.theme-transitioning .cv-root,
html.theme-transitioning .cv-root *,
body.theme-transitioning .cv-root,
body.theme-transitioning .cv-root * {
  transition-property: color, background-color, border-color, box-shadow, fill, stroke;
  transition-duration: var(--theme-duration, 220ms);
  transition-timing-function: ease;
}

html.theme-transitioning .cv-root .cv-contact a,
body.theme-transitioning .cv-root .cv-contact a,
html.theme-transitioning .cv-root .cv-theme,
html.theme-transitioning .cv-root .cv-print,
body.theme-transitioning .cv-root .cv-theme,
body.theme-transitioning .cv-root .cv-print {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}
</style>
